import { t } from '@lingui/macro';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { TIntroducerApplication } from 'lendingApi/applications.types';
import { TRejectOfferReasons } from 'lendingApi/lendingApi.types';
import { useGetRejectOfferReasons } from 'lendingApi/lendingApiHooks';
import { getDefaultRejectReason } from './useRejectReason.helpers';

const translateRejectOfferReason = (reason: TRejectOfferReasons) => {
  switch (reason) {
    case 'funds_are_no_longer_required':
      return t`because the funds were no longer required`;
    case 'required_a_larger_amount':
      return t`because the applicant required a larger amount`;
    case 'interest_rate_too_high':
      return t`because the interest rate was too high for the applicant`;
    case 'received_a_better_offer':
      return t`because the applicant received a more competitive offer`;
    case 'required_a_longer_term':
      return t`because the applicant required a longer term`;
    case 'unable_to_contact_customer':
      return t`because the applicant could not be contacted`;
    case `no_reason_given`:
      return t`and no reason was given`;
    case `funds_needed_after_timeout_window_expires`:
      return t`because funds were needed after offer expired`;
    default:
      return reason;
  }
};

export const useRejectReason = (
  application: TIntroducerApplication | undefined,
) => {
  const { introducerId } = useIntroducerContext();
  const hasMultipleOffers =
    application &&
    application?.formal_offer_sets?.[0]?.formal_offers?.length > 1;

  const { data: rejectionReason } = useGetRejectOfferReasons(
    introducerId,
    application?.application_uid,
    application?.status,
  );

  if (application?.status !== 'offer_rejected') return;

  const reason = rejectionReason?.data?.reject_offer_reasons[0]?.reason;

  if (reason && reason !== 'other') {
    if (hasMultipleOffers) {
      return t`These offers have been rejected ${translateRejectOfferReason(
        reason,
      )}.`;
    } else {
      return t`This offer was rejected ${translateRejectOfferReason(reason)}.`;
    }
  }

  return getDefaultRejectReason(!!hasMultipleOffers);
};
